import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Layers, Plus, ScrollText, Search, ShieldAlert, ShieldOff } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";
import { DatePicker } from "@/components/ui/DatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MobileSortBar, SortableTh, useTableSort } from "@/components/ui/TableSort";
import { useAuthStore } from "@/store/auth";
import { canManageAgreementGroups, canManageContractors } from "@/features/contractors/utils";
import { AgreementGroupsManagerModal } from "@/features/contractors/components/AgreementGroupsManagerModal";
import {
  AcceptanceDeadlineDaysField,
  acceptanceDeadlineDaysError,
} from "@/features/contractors/components/AcceptanceDeadlineDaysField";
import { NO_GROUP_KEY, NO_GROUP_LABEL, type AgreementGroupBrief } from "@/features/contractors/agreementGroups";

interface Contractor {
  id: string;
  name: string;
  short_name: string;
}

interface Agreement {
  id: string;
  contractor: string;
  contractor_name: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  is_confidential: boolean;
  // Plik umowy prosto na liście — po to tu jest, żeby „pobrać umowy" nie wymagało
  // wchodzenia w każdą z osobna (i żeby brak skanu było widać od razu).
  document_managed_file: string | null;
  document_managed_file_detail: (ManagedFileRef & { original_filename?: string }) | null;
  line_items_count: number;
  // Nazwy pozycji z cennika umowy — po nich też szuka wyszukiwarka nad listą
  // ("która umowa obejmuje MDCP?"). Same nazwy, bez stawek.
  line_item_names: string[];
  // Kwota minimalna zamówienia — null = bez minimum.
  min_order_amount?: string | null;
  requires_insurance: boolean;
  min_insurance_sum?: string | null;
  insurance_status: "not_required" | "missing" | "expired" | "expiring_soon" | "valid";
  // Ostatnia polisa OC podwykonawcy niezależnie od wymogu umowy — insurance_status
  // dla umowy bez wymogu mówi tylko „not_required”.
  oc_policy_status: OcPolicyStatus;
  oc_policy_valid_until: string | null;
  // Grupy umów ramowych — brak pola dla kont podwykonawcy, pusta lista = bez grupy.
  groups?: AgreementGroupBrief[];
  created_at: string;
}

type OcPolicyStatus = "none" | "expired" | "expiring_soon" | "valid";

// Filtry tak/nie — "" = wszystkie.
type YesNoFilter = "" | "yes" | "no";

// „Posiada polisę OC” = ostatnio wgrana polisa jeszcze nie wygasła.
const hasCurrentOcPolicy = (a: Agreement) =>
  a.oc_policy_status === "valid" || a.oc_policy_status === "expiring_soon";

const matchesYesNo = (filter: YesNoFilter, value: boolean) =>
  !filter || (filter === "yes") === value;

interface AgreementForm {
  contractor: string;
  title: string;
  signed_date: string;
  valid_until: string;
  is_confidential: boolean;
  payment_term_days: string;
  // Termin na przyjęcie zamówienia (dni kalendarzowe od wysłania); puste = domyślne 5 dni.
  acceptance_deadline_days: string;
  // Checkbox „Kwota minimalna zamówienia” + kwota; odznaczony = bez minimum (null).
  has_min_order_amount: boolean;
  min_order_amount: string;
  auto_accept: boolean;
  allow_partial_pock: boolean;
  allow_external_costs: boolean;
  allow_official_fee_requests: boolean;
  requires_insurance: boolean;
  min_insurance_sum: string;
  requires_weekly_reports: boolean;
  report_template: "standard" | "complex_lot";
  report_day_of_week: string;
  report_due_time: string;
}

// Detail payload used to prefill the form when creating "na podstawie
// istniejącej". Some fields are absent for non-directors (rate/terms
// visibility), hence the optionals.
interface AgreementDetail {
  id: string;
  contractor: string;
  title: string;
  signed_date: string;
  valid_until: string | null;
  is_confidential: boolean;
  payment_term_days: number | null;
  acceptance_deadline_days?: number | null;
  min_order_amount?: string | null;
  auto_accept: boolean;
  allow_partial_pock?: boolean;
  allow_external_costs: boolean;
  allow_official_fee_requests: boolean;
  requires_insurance: boolean;
  min_insurance_sum: string | null;
  requires_weekly_reports: boolean;
  report_template: "standard" | "complex_lot";
  report_day_of_week: number | null;
  report_due_time: string | null;
}

const formatMinOrderAmount = (v: string) =>
  parseFloat(v).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = (): AgreementForm => ({
  contractor: "",
  title: "",
  signed_date: "",
  valid_until: "",
  is_confidential: true,
  payment_term_days: "",
  acceptance_deadline_days: "",
  has_min_order_amount: false,
  min_order_amount: "",
  auto_accept: true,
  allow_partial_pock: false,
  allow_external_costs: false,
  allow_official_fee_requests: false,
  requires_insurance: false,
  min_insurance_sum: "",
  requires_weekly_reports: false,
  report_template: "standard",
  report_day_of_week: "",
  report_due_time: "",
});

// Nazwy pozycji, które trafiły w szukaną frazę — trzy pierwsze plus licznik reszty,
// żeby wiersz umowy z długim cennikiem nie rozjechał tabeli.
function formatLineItemHits(hits: string[]): string {
  const head = hits.slice(0, 3).join(", ");
  return hits.length > 3 ? `${head} (+${hits.length - 3})` : head;
}

const OC_PILL_RED =
  "inline-flex items-center gap-0.5 text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full px-1.5 py-0.5";
const OC_PILL_YELLOW =
  "inline-flex items-center gap-0.5 text-[10px] font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full px-1.5 py-0.5";

// Kolumna OC: wymóg umowy + stan polisy podwykonawcy. Na czerwono/żółto TYLKO
// przy umowie wymagającej OC — umowa bez wymogu pokazuje polisę neutralnie,
// bo jej brak niczego nie blokuje (bramka zamówień patrzy na requires_insurance).
function OcCell({ a }: { a: Agreement }) {
  const until = a.oc_policy_valid_until
    ? new Date(a.oc_policy_valid_until).toLocaleDateString("pl")
    : null;
  let policy: React.ReactNode;
  if (a.requires_insurance && a.insurance_status === "missing") {
    policy = <span className={OC_PILL_RED} title="Brak polisy OC"><ShieldOff size={10} /> Brak OC</span>;
  } else if (a.requires_insurance && a.insurance_status === "expired") {
    policy = <span className={OC_PILL_RED} title={`Polisa OC wygasła ${until}`}><ShieldAlert size={10} /> OC wygasła</span>;
  } else if (a.requires_insurance && a.insurance_status === "expiring_soon") {
    policy = <span className={OC_PILL_YELLOW} title={`Polisa OC wygasa ${until}`}><ShieldAlert size={10} /> OC do {until}</span>;
  } else if (a.oc_policy_status === "valid" || a.oc_policy_status === "expiring_soon") {
    policy = <span className="text-gray-600 dark:text-gray-400">polisa do {until}</span>;
  } else if (a.oc_policy_status === "expired") {
    policy = <span className="text-gray-400 dark:text-gray-500">polisa wygasła {until}</span>;
  } else {
    policy = <span className="text-gray-400 dark:text-gray-500">brak polisy</span>;
  }
  return (
    <div className="flex flex-col items-start gap-0.5">
      {a.requires_insurance ? (
        <span
          className="text-gray-700 dark:text-gray-300"
          title={
            a.min_insurance_sum != null
              ? `Umowa wymaga polisy OC — min. suma ${formatMinOrderAmount(a.min_insurance_sum)} PLN`
              : "Umowa wymaga polisy OC"
          }
        >
          Wymagana
        </span>
      ) : (
        <span className="text-gray-400 dark:text-gray-500" title="Umowa nie wymaga polisy OC — jej brak niczego nie blokuje">
          Niewymagana
        </span>
      )}
      {policy}
    </div>
  );
}

function isExpired(validUntil: string | null): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

type SortField =
  | "title"
  | "contractor_name"
  | "signed_date"
  | "valid_until"
  | "line_items_count"
  | "status";

const SORT_LABELS: Record<SortField, string> = {
  title: "Tytuł umowy",
  contractor_name: "Podwykonawca",
  signed_date: "Data podpisania",
  valid_until: "Ważna do",
  line_items_count: "Pozycje",
  status: "Status",
};

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

function compareAgreements(a: Agreement, b: Agreement, field: SortField): number {
  switch (field) {
    case "title":
      return a.title.localeCompare(b.title, "pl");
    case "contractor_name":
      return a.contractor_name.localeCompare(b.contractor_name, "pl");
    case "signed_date":
      return a.signed_date.localeCompare(b.signed_date);
    case "valid_until":
      // Open-ended agreements (no valid_until) sort as the farthest future date.
      if (a.valid_until === b.valid_until) return 0;
      if (!a.valid_until) return 1;
      if (!b.valid_until) return -1;
      return a.valid_until.localeCompare(b.valid_until);
    case "line_items_count":
      return a.line_items_count - b.line_items_count;
    case "status":
      return Number(isExpired(a.valid_until)) - Number(isExpired(b.valid_until));
  }
}

export function AgreementsListPage() {
  const user = useAuthStore((s) => s.user);
  // CEO / technical director by role, or the CEO-granted 'Podwykonawcy i umowy
  // ramowe' permission (mirrors backend CanManageContractors).
  const canManageDoc = canManageContractors(user);
  // Gates the "Poufna" badge — mirrors rate_visibility: confidential agreements
  // are a director-level concern, others shouldn't even see the flag.
  const isDirector = user?.role === "director";
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterContractor, setFilterContractor] = useState("");
  const [filterStatus, setFilterStatus] = useState<"" | "active" | "expired">("");
  // Filtr po grupie umów; NO_GROUP_KEY = umowy bez żadnej grupy.
  const [filterGroup, setFilterGroup] = useState("");
  const [filterOcRequired, setFilterOcRequired] = useState<YesNoFilter>("");
  const [filterOcPolicy, setFilterOcPolicy] = useState<YesNoFilter>("");
  const [showGroups, setShowGroups] = useState(false);
  const canManageGroups = canManageAgreementGroups(user);
  const [signedFrom, setSignedFrom] = useState("");
  const [signedTo, setSignedTo] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const { sortField, sortDir, handleSort, setField, toggleDir } = useTableSort<SortField>();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<AgreementForm>(emptyForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [docFile, setDocFile] = useState<File | null>(null);
  // "Na podstawie istniejącej" — source agreement whose fields prefill the
  // form and whose line items the backend copies onto the new agreement.
  const [basedOnExisting, setBasedOnExisting] = useState(false);
  const [sourceAgreementId, setSourceAgreementId] = useState("");
  const [sourceLoading, setSourceLoading] = useState(false);

  const { data: agreements, isLoading } = useQuery({
    queryKey: ["agreements"],
    queryFn: () =>
      apiClient
        .get<{ data: Agreement[] }>("/agreements/")
        .then((r) => r.data.data),
  });

  const { data: contractors } = useQuery({
    queryKey: ["contractors"],
    queryFn: () =>
      apiClient
        .get<{ data: Contractor[] }>("/contractors/")
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
    enabled: canManageDoc && showCreate,
  });

  const createMutation = useMutation({
    mutationFn: (payload: AgreementForm) => {
      const fd = new FormData();
      fd.append("contractor", payload.contractor);
      fd.append("title", payload.title);
      fd.append("signed_date", payload.signed_date);
      if (payload.valid_until) fd.append("valid_until", payload.valid_until);
      fd.append("is_confidential", String(payload.is_confidential));
      if (payload.payment_term_days) fd.append("payment_term_days", payload.payment_term_days);
      if (payload.acceptance_deadline_days) fd.append("acceptance_deadline_days", payload.acceptance_deadline_days);
      if (payload.has_min_order_amount && payload.min_order_amount) fd.append("min_order_amount", payload.min_order_amount);
      fd.append("auto_accept", String(payload.auto_accept));
      fd.append("allow_partial_pock", String(payload.allow_partial_pock));
      fd.append("allow_external_costs", String(payload.allow_external_costs));
      fd.append("allow_official_fee_requests", String(payload.allow_official_fee_requests));
      fd.append("requires_insurance", String(payload.requires_insurance));
      if (payload.requires_insurance && payload.min_insurance_sum) fd.append("min_insurance_sum", payload.min_insurance_sum);
      fd.append("requires_weekly_reports", String(payload.requires_weekly_reports));
      fd.append("report_template", payload.report_template);
      if (payload.report_day_of_week !== "") fd.append("report_day_of_week", payload.report_day_of_week);
      if (payload.report_due_time) fd.append("report_due_time", payload.report_due_time);
      if (docFile) fd.append("document_file", docFile);
      // Document is deliberately NOT copied from the source agreement —
      // only line items are (server-side).
      if (basedOnExisting && sourceAgreementId)
        fd.append("copy_line_items_from", sourceAgreementId);
      return apiClient
        .post<{ data: Agreement }>("/agreements/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreements"] });
      setShowCreate(false);
      setForm(emptyForm());
      setDocFile(null);
      setFormErrors({});
      setBasedOnExisting(false);
      setSourceAgreementId("");
    },
    onError: (err: {
      response?: { data?: { errors?: Record<string, string[]> } };
    }) => {
      const apiErrors = err.response?.data?.errors ?? {};
      const flat: Record<string, string> = {};
      for (const [k, v] of Object.entries(apiErrors)) {
        flat[k] = Array.isArray(v) ? v[0] : String(v);
      }
      setFormErrors(flat);
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.contractor) errs.contractor = "Wybierz podwykonawcę";
    if (!form.title.trim()) errs.title = "Tytuł jest wymagany";
    if (!form.signed_date) errs.signed_date = "Data podpisania jest wymagana";
    if (form.has_min_order_amount && !(parseFloat(form.min_order_amount) > 0))
      errs.min_order_amount = "Podaj kwotę minimalną zamówienia większą od zera";
    const acceptanceError = acceptanceDeadlineDaysError(form.acceptance_deadline_days);
    if (acceptanceError) errs.acceptance_deadline_days = acceptanceError;
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const upd = <K extends keyof AgreementForm>(k: K, v: AgreementForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  // Prefill the form from an existing agreement. The document file is not
  // carried over; fields hidden from this user (rate visibility) keep their
  // current values.
  const loadSourceAgreement = async (id: string) => {
    setSourceAgreementId(id);
    setFormErrors((p) => ({ ...p, source_agreement: "" }));
    if (!id) return;
    setSourceLoading(true);
    try {
      const r = await apiClient.get<{ data: AgreementDetail }>(`/agreements/${id}/`);
      const a = r.data.data;
      setForm((p) => ({
        ...p,
        contractor: a.contractor,
        title: a.title,
        signed_date: a.signed_date ?? "",
        valid_until: a.valid_until ?? "",
        is_confidential: a.is_confidential,
        payment_term_days: a.payment_term_days != null ? String(a.payment_term_days) : "",
        acceptance_deadline_days: a.acceptance_deadline_days != null ? String(a.acceptance_deadline_days) : "",
        has_min_order_amount: a.min_order_amount != null,
        min_order_amount: a.min_order_amount != null ? String(a.min_order_amount) : "",
        auto_accept: a.auto_accept,
        allow_partial_pock: a.allow_partial_pock ?? p.allow_partial_pock,
        allow_external_costs: a.allow_external_costs,
        allow_official_fee_requests: a.allow_official_fee_requests,
        requires_insurance: a.requires_insurance,
        min_insurance_sum: a.min_insurance_sum != null ? String(a.min_insurance_sum) : "",
        requires_weekly_reports: a.requires_weekly_reports,
        report_template: a.report_template,
        report_day_of_week: a.report_day_of_week != null ? String(a.report_day_of_week) : "",
        report_due_time: a.report_due_time ? a.report_due_time.slice(0, 5) : "",
      }));
    } catch {
      setFormErrors((p) => ({
        ...p,
        source_agreement: "Nie udało się wczytać danych umowy.",
      }));
    } finally {
      setSourceLoading(false);
    }
  };

  // Options for the "na podstawie istniejącej" picker — searchable by
  // agreement number/title and contractor name.
  const sourceAgreementOptions = useMemo(
    () =>
      (agreements ?? []).map((a) => ({
        value: a.id,
        label: `${a.title} — ${a.contractor_name}`,
      })),
    [agreements],
  );

  // Filter dropdown options come from the loaded list itself — a contractor
  // user only sees their own agreements, so no separate /contractors/ fetch.
  const contractorFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of agreements ?? []) {
      if (!seen.has(a.contractor)) seen.set(a.contractor, a.contractor_name);
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((x, y) => x.label.localeCompare(y.label, "pl"));
  }, [agreements]);

  // Grupy do filtra — z samych wierszy (jak podwykonawcy wyżej), więc bez
  // dodatkowego zapytania; „Bez przypisanej grupy" tylko gdy taka umowa istnieje.
  const groupFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    let ungrouped = false;
    for (const a of agreements ?? []) {
      const gs = a.groups ?? [];
      if (gs.length === 0) ungrouped = true;
      for (const g of gs) if (!seen.has(g.id)) seen.set(g.id, g.name);
    }
    const out = [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((x, y) => x.label.localeCompare(y.label, "pl"));
    if (ungrouped) out.push({ value: NO_GROUP_KEY, label: NO_GROUP_LABEL });
    return out;
  }, [agreements]);

  // Fraza wyszukiwania współdzielona przez filtr i wiersze listy (te podświetlają
  // pozycje, które trafiły w zapytanie — inaczej nie widać, czemu umowa się pokazała).
  const query = search.trim().toLowerCase();

  const matchedLineItems = (a: Agreement): string[] =>
    query ? (a.line_item_names ?? []).filter((n) => n.toLowerCase().includes(query)) : [];

  const filtered = useMemo(() => {
    const q = query;
    const list = (agreements ?? []).filter((a) => {
      if (
        q &&
        !a.title.toLowerCase().includes(q) &&
        !a.contractor_name.toLowerCase().includes(q) &&
        !(a.line_item_names ?? []).some((n) => n.toLowerCase().includes(q))
      )
        return false;
      if (filterContractor && a.contractor !== filterContractor) return false;
      if (filterGroup) {
        const gs = a.groups ?? [];
        if (filterGroup === NO_GROUP_KEY ? gs.length > 0 : !gs.some((g) => g.id === filterGroup))
          return false;
      }
      if (filterStatus && (filterStatus === "expired") !== isExpired(a.valid_until)) return false;
      if (!matchesYesNo(filterOcRequired, a.requires_insurance)) return false;
      if (!matchesYesNo(filterOcPolicy, hasCurrentOcPolicy(a))) return false;
      // ISO yyyy-mm-dd strings compare correctly as plain strings.
      if (signedFrom && a.signed_date < signedFrom) return false;
      if (signedTo && a.signed_date > signedTo) return false;
      if (validFrom && (!a.valid_until || a.valid_until < validFrom)) return false;
      if (validTo && (!a.valid_until || a.valid_until > validTo)) return false;
      return true;
    });
    if (!sortField) return list;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => dir * compareAgreements(a, b, sortField));
  }, [agreements, query, filterContractor, filterGroup, filterStatus, filterOcRequired, filterOcPolicy, signedFrom, signedTo, validFrom, validTo, sortField, sortDir]);

  const hasActiveFilters =
    !!search || !!filterContractor || !!filterGroup || !!filterStatus ||
    !!filterOcRequired || !!filterOcPolicy ||
    !!signedFrom || !!signedTo || !!validFrom || !!validTo;

  const clearFilters = () => {
    setSearch("");
    setFilterContractor("");
    setFilterGroup("");
    setFilterStatus("");
    setFilterOcRequired("");
    setFilterOcPolicy("");
    setSignedFrom("");
    setSignedTo("");
    setValidFrom("");
    setValidTo("");
  };

  if (isLoading) return <PageLoader />;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ScrollText size={22} className="text-[var(--color-primary)]" />
          <div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Umowy ramowe
            </h1>
              <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {hasActiveFilters
                ? `${filtered.length} z ${(agreements ?? []).length} umów`
                : `${(agreements ?? []).length} umów`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canManageGroups && (
            <Button
              variant="secondary"
              onClick={() => setShowGroups(true)}
              title="Grupy umów ramowych — zakładanie, zmiana nazwy, usuwanie"
            >
              <Layers size={15} /> Grupy
            </Button>
          )}
          {canManageDoc && (
            <Button
              onClick={() => {
                setShowCreate(true);
                setForm(emptyForm());
                setFormErrors({});
                setBasedOnExisting(false);
                setSourceAgreementId("");
              }}
            >
              <Plus size={15} /> Nowa umowa
            </Button>
          )}
        </div>
      </div>

      {canManageGroups && (
        <AgreementGroupsManagerModal open={showGroups} onClose={() => setShowGroups(false)} />
      )}

      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          type="text"
          placeholder="Szukaj po tytule, podwykonawcy lub nazwie pozycji z umowy..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="block w-full rounded-lg border border-gray-300 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5">
        <div className="w-64 max-w-full">
          <SearchableSelect
            placeholder="Wszyscy podwykonawcy"
            value={filterContractor}
            onChange={(e) => setFilterContractor(e.target.value)}
            options={contractorFilterOptions}
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Podpisana:</span>
          <DatePicker
            className="w-36"
            value={signedFrom}
            onChange={setSignedFrom}
            max={signedTo || undefined}
            placeholder="od"
            aria-label="Data podpisania od"
          />
          <span className="text-gray-400 dark:text-gray-500">–</span>
          <DatePicker
            className="w-36"
            value={signedTo}
            onChange={setSignedTo}
            min={signedFrom || undefined}
            placeholder="do"
            aria-label="Data podpisania do"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Ważna do:</span>
          <DatePicker
            className="w-36"
            value={validFrom}
            onChange={setValidFrom}
            max={validTo || undefined}
            placeholder="od"
            aria-label="Ważna do — od"
          />
          <span className="text-gray-400 dark:text-gray-500">–</span>
          <DatePicker
            className="w-36"
            value={validTo}
            onChange={setValidTo}
            min={validFrom || undefined}
            placeholder="do"
            aria-label="Ważna do — do"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as "" | "active" | "expired")}
          className={SELECT_CLS}
          aria-label="Status umowy"
        >
          <option value="">Wszystkie statusy</option>
          <option value="active">Aktywna</option>
          <option value="expired">Wygasła</option>
        </select>
        {groupFilterOptions.length > 0 && (
          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className={SELECT_CLS}
            aria-label="Grupa umów"
          >
            <option value="">Wszystkie grupy</option>
            {groupFilterOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        {/* OC — te same osoby co znaczniki „Brak OC” / kolumna OC (zarządzający umowami). */}
        {canManageDoc && (
          <>
            <label className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Wymagane OC:</span>
              <select
                value={filterOcRequired}
                onChange={(e) => setFilterOcRequired(e.target.value as YesNoFilter)}
                className={SELECT_CLS}
                aria-label="Wymagane OC"
              >
                <option value="">Wszystkie</option>
                <option value="yes">Tak</option>
                <option value="no">Nie</option>
              </select>
            </label>
            <label
              className="flex items-center gap-1.5"
              title="Czy podwykonawca ma aktualną (niewygasłą) polisę OC — niezależnie od tego, czy umowa jej wymaga"
            >
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Posiada polisę OC:</span>
              <select
                value={filterOcPolicy}
                onChange={(e) => setFilterOcPolicy(e.target.value as YesNoFilter)}
                className={SELECT_CLS}
                aria-label="Posiada polisę OC"
              >
                <option value="">Wszystkie</option>
                <option value="yes">Tak</option>
                <option value="no">Nie</option>
              </select>
            </label>
          </>
        )}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-12 text-center">
          <ScrollText size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? "Brak umów spełniających wybrane kryteria."
              : "Brak umów ramowych."}
          </p>
        </div>
      ) : (
        <>
          <MobileSortBar
            id="agreements-sort"
            labels={SORT_LABELS}
            sortField={sortField}
            sortDir={sortDir}
            onFieldChange={setField}
            onDirToggle={toggleDir}
          />
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {filtered.map((a) => {
              const expired = isExpired(a.valid_until);
              const hits = matchedLineItems(a);
              return (
                <div key={a.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <Link to={`/agreements/${a.id}`} className="font-medium text-[var(--color-primary)] hover:underline block truncate">
                        {a.title}
                      </Link>
                      <Link to={`/contractors/${a.contractor}`} className="text-xs text-gray-500 dark:text-gray-400 hover:underline truncate block mt-0.5">
                        {a.contractor_name}
                      </Link>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {new Date(a.signed_date).toLocaleDateString("pl")}
                        {a.valid_until ? ` → ${new Date(a.valid_until).toLocaleDateString("pl")}` : ""}
                      </p>
                      {a.min_order_amount != null && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <span className="text-gray-400 dark:text-gray-500">Min. zamówienie: </span>
                          {formatMinOrderAmount(a.min_order_amount)} PLN
                        </p>
                      )}
                      {hits.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <span className="text-gray-400 dark:text-gray-500">Pozycje: </span>
                          {formatLineItemHits(hits)}
                        </p>
                      )}
                      {canManageDoc && (
                        <div className="mt-1">
                          {a.document_managed_file_detail ? (
                            <ManagedFileLink
                              file={a.document_managed_file_detail}
                              icon={<FileText size={11} />}
                            >
                              Pobierz umowę
                            </ManagedFileLink>
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              Brak skanu umowy
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge color={expired ? "red" : "green"}>{expired ? "Wygasła" : "Aktywna"}</Badge>
                      {a.is_confidential && isDirector && (
                        <span title="Umowa poufna — ukryta przed pozostałymi pracownikami. Ty ją widzisz i możesz pobrać jej plik.">
                          <Badge color="gray">Poufna</Badge>
                        </span>
                      )}
                      {canManageDoc && a.requires_insurance && (a.insurance_status === "missing" || a.insurance_status === "expired") && (
                        <Badge color="red">Brak OC</Badge>
                      )}
                      {(a.groups ?? []).map((g) => (
                        <Badge key={g.id} color="blue">{g.name}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <SortableTh field="title" label="Tytuł umowy" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="contractor_name" label="Podwykonawca" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="signed_date" label="Data podpisania" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="valid_until" label="Ważna do" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="line_items_count" label="Pozycje" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">Grupy</th>
                  {canManageDoc && (
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">Plik umowy</th>
                  )}
                  {canManageDoc && (
                    <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">Polisa OC</th>
                  )}
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((a) => {
                  const expired = isExpired(a.valid_until);
                  const hits = matchedLineItems(a);
                  return (
                    <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <Link to={`/agreements/${a.id}`} className="font-medium text-[var(--color-primary)] hover:underline">{a.title}</Link>
                        {a.min_order_amount != null && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5" title="Kwota minimalna zamówienia z umowy — zapotrzebowanie o niższej wartości dostaje ostrzeżenie">
                            <span className="text-gray-400 dark:text-gray-500">Min. zamówienie: </span>
                            {formatMinOrderAmount(a.min_order_amount)} PLN
                          </div>
                        )}
                        {/* Trafienie w cennik — bez tego wiersz wygląda na niepasujący do frazy. */}
                        {hits.length > 0 && (
                          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                            <span className="text-gray-400 dark:text-gray-500">Pozycje: </span>
                            {formatLineItemHits(hits)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        <Link to={`/contractors/${a.contractor}`} className="hover:underline text-gray-600 dark:text-gray-400">{a.contractor_name}</Link>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{new Date(a.signed_date).toLocaleDateString("pl")}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.valid_until ? new Date(a.valid_until).toLocaleDateString("pl") : "—"}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{a.line_items_count}</td>
                      <td className="px-4 py-3">
                        {(a.groups ?? []).length === 0 ? (
                          <span className="text-gray-400 dark:text-gray-500" title="Umowa bez grupy — w wycenie kosztorysu pod „Bez przypisanej grupy”">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(a.groups ?? []).map((g) => (
                              <Badge key={g.id} color="blue">{g.name}</Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      {canManageDoc && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          {a.document_managed_file_detail ? (
                            <ManagedFileLink
                              file={a.document_managed_file_detail}
                              icon={<FileText size={11} />}
                            >
                              Pobierz
                            </ManagedFileLink>
                          ) : (
                            <span
                              className="text-gray-400 dark:text-gray-500"
                              title="Nikt nie wgrał jeszcze skanu tej umowy"
                            >
                              brak skanu
                            </span>
                          )}
                        </td>
                      )}
                      {canManageDoc && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <OcCell a={a} />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {expired ? <Badge color="red">Wygasła</Badge> : <Badge color="green">Aktywna</Badge>}
                          {a.is_confidential && isDirector && (
                            <span title="Umowa poufna — ukryta przed pozostałymi pracownikami. Ty ją widzisz i możesz pobrać jej plik.">
                              <Badge color="gray">Poufna</Badge>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create modal */}
      <Modal
        open={showCreate}
        onClose={() => { setShowCreate(false); setDocFile(null); setBasedOnExisting(false); setSourceAgreementId(""); }}
        title="Nowa umowa ramowa"
        size="md"
      >
        <div className="flex flex-col gap-4">
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={basedOnExisting}
                onChange={(e) => {
                  setBasedOnExisting(e.target.checked);
                  if (!e.target.checked) setSourceAgreementId("");
                }}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Na podstawie istniejącej umowy
              </span>
            </label>
            {basedOnExisting && (
              <div className="mt-2 ml-6">
                <SearchableSelect
                  label="Umowa źródłowa"
                  placeholder="— wyszukaj po numerze lub tytule —"
                  value={sourceAgreementId}
                  onChange={(e) => loadSourceAgreement(e.target.value)}
                  options={sourceAgreementOptions}
                  error={formErrors.source_agreement || undefined}
                />
                <p className="text-xs text-gray-400 mt-1">
                  {sourceLoading
                    ? "Wczytywanie danych umowy..."
                    : "Pola formularza i pozycje realizacyjne zostaną przeniesione (możesz je poprawić). Dokument umowy nie zostanie skopiowany."}
                </p>
              </div>
            )}
          </div>

          <SearchableSelect
            label="Podwykonawca *"
            placeholder="— wybierz —"
            value={form.contractor}
            onChange={(e) => upd("contractor", e.target.value)}
            options={(contractors ?? []).map((c) => ({ value: c.id, label: c.name }))}
            error={formErrors.contractor}
          />

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Tytuł umowy<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="text"
              className={`block w-full rounded-md border px-3 py-2 text-sm ${formErrors.title ? "border-red-400" : "border-gray-300"}`}
              value={form.title}
              onChange={(e) => upd("title", e.target.value)}
              placeholder="np. Umowa ramowa na usługi telekomunikacyjne 2024"
            />
            {formErrors.title && (
              <p className="text-xs text-red-600 mt-0.5">{formErrors.title}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Data podpisania<span className="text-red-500 ml-0.5">*</span>
              </label>
              <DatePicker
                className={formErrors.signed_date ? "border-red-400" : undefined}
                value={form.signed_date}
                onChange={(v) => upd("signed_date", v)}
              />
              {formErrors.signed_date && (
                <p className="text-xs text-red-600 mt-0.5">
                  {formErrors.signed_date}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                Ważna do (opcjonalnie)
              </label>
              <DatePicker
                value={form.valid_until}
                onChange={(v) => upd("valid_until", v)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Termin płatności (dni od wystawienia POCK)
            </label>
            <input
              type="number"
              min="0"
              placeholder="np. 30"
              className="block w-40 rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={form.payment_term_days}
              onChange={(e) => upd("payment_term_days", e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-0.5">
              Używany do planowania wydatków POCK w cashflow
            </p>
          </div>

          <AcceptanceDeadlineDaysField
            value={form.acceptance_deadline_days}
            onChange={(v) => upd("acceptance_deadline_days", v)}
            error={formErrors.acceptance_deadline_days}
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_confidential}
              onChange={(e) => upd("is_confidential", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Umowa poufna (widoczna tylko dla Dyrektora)
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_accept}
              onChange={(e) => upd("auto_accept", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Automatyczne przyjęcie zleceń (podwykonawca dostaje tylko powiadomienie)
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.allow_partial_pock}
              onChange={(e) => upd("allow_partial_pock", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Zezwalaj na częściowy POCK (odznacz, aby wymagać 100% zakresu przy każdym wniosku)
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.allow_external_costs}
              onChange={(e) => upd("allow_external_costs", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Zezwalaj na koszty zewnętrzne (podwykonawca może składać wnioski)
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.allow_official_fee_requests}
              onChange={(e) => upd("allow_official_fee_requests", e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Zezwalaj na opłaty urzędowe (podwykonawca może wnioskować o opłatę urzędową)
            </span>
          </label>

          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={form.has_min_order_amount}
                onChange={(e) => upd("has_min_order_amount", e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Kwota minimalna zamówienia
              </span>
            </label>
            {form.has_min_order_amount && (
              <div className="ml-6">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Kwota minimalna (PLN)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="np. 5000"
                  className={`block w-48 rounded-md border px-3 py-1.5 text-sm ${formErrors.min_order_amount ? "border-red-400" : "border-gray-300"}`}
                  value={form.min_order_amount}
                  onChange={(e) => upd("min_order_amount", e.target.value)}
                />
                {formErrors.min_order_amount && (
                  <p className="text-xs text-red-600 mt-0.5">{formErrors.min_order_amount}</p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">
                  Zapotrzebowanie o niższej wartości dostanie ostrzeżenie przy proponowaniu podwykonawcy i zatwierdzaniu (nie blokuje).
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={form.requires_insurance}
                onChange={(e) => upd("requires_insurance", e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Wymagana polisa OC
              </span>
            </label>
            {form.requires_insurance && (
              <div className="ml-6">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                  Minimalna suma ubezpieczenia (PLN, opcjonalnie)
                </label>
                <input
                  type="number"
                  min="0"
                  placeholder="np. 1000000"
                  className="block w-48 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  value={form.min_insurance_sum}
                  onChange={(e) => upd("min_insurance_sum", e.target.value)}
                />
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input
                type="checkbox"
                checked={form.requires_weekly_reports}
                onChange={(e) => upd("requires_weekly_reports", e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Wymagaj cotygodniowych raportów postępu
              </span>
            </label>
            {form.requires_weekly_reports && (
              <div className="ml-6 flex flex-col gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Typ raportu
                  </label>
                  <select
                    className="block rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                    value={form.report_template}
                    onChange={(e) => upd("report_template", e.target.value as "standard" | "complex_lot")}
                  >
                    <option value="standard">Standardowy</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                      Dzień składania raportu
                    </label>
                    <select
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                      value={form.report_day_of_week}
                      onChange={(e) => upd("report_day_of_week", e.target.value)}
                    >
                      <option value="">— bez określenia —</option>
                      {["Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota", "Niedziela"].map((d, i) => (
                        <option key={i} value={String(i)}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                      Godzina deadline
                    </label>
                    <input
                      type="time"
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                      value={form.report_due_time}
                      onChange={(e) => upd("report_due_time", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Dokument umowy (PDF, opcjonalnie)
            </label>
            <FileDropZone
              files={docFile ? [docFile] : []}
              onFilesChange={(fs) => setDocFile(fs[0] ?? null)}
              accept=".pdf,.doc,.docx"
            />
          </div>

          {createMutation.isError && Object.keys(formErrors).length === 0 && (
            <p className="text-sm text-red-600">
              Błąd zapisu. Sprawdź dane i spróbuj ponownie.
            </p>
          )}

          <div className="flex gap-3 pt-1">
            <Button
              loading={createMutation.isPending}
              onClick={() => {
                if (validate()) createMutation.mutate(form);
              }}
            >
              Utwórz umowę
            </Button>
            <Button variant="secondary" onClick={() => { setShowCreate(false); setDocFile(null); setBasedOnExisting(false); setSourceAgreementId(""); }}>
              Anuluj
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
